import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';

interface Props {
  visible: boolean;
  relatives: any[];
  onClose: () => void;
  onSubmit: (contactId: string, phone: string) => Promise<void>;
}

export function EditEmergencyContactModal({ visible, relatives, onClose, onSubmit }: Props) {
  const [selectedId, setSelectedId] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const options = useMemo(() => {
    return (relatives || []).map((r: any) => {
      const first = r.FirstName || r.firstName || '';
      const last = r.LastName || r.lastName || '';
      const relation = r.RelationName || r.relationName || r.RelationCode || r.relationCode || 'Guardian';
      const id = r.Id || r.id;
      const currentPhone = r.CellPhone || r.cellPhone || r.Phone || r.phone || '';
      return { id, label: `${first} ${last}`.trim(), relation, phone: currentPhone };
    }).filter((o: any) => !!o.id);
  }, [relatives]);

  useEffect(() => {
    if (visible) {
      const emergency = (relatives || []).find((r: any) => r.IsEmergencyContact ?? r.isEmergencyContact);
      const emergencyId = emergency?.Id || emergency?.id;
      const initial = options.find((o: any) => o.id === emergencyId) || options[0];
      if (initial) {
        setSelectedId(initial.id);
        setPhone(initial.phone);
      } else {
        setSelectedId('');
        setPhone('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleSelect = (opt: { id: string; phone: string }) => {
    setSelectedId(opt.id);
    setPhone(opt.phone);
  };

  const handleSave = async () => {
    if (!selectedId) {
      Alert.alert('Error', 'Please select a contact person.');
      return;
    }
    if (!phone.trim()) {
      Alert.alert('Error', 'Please enter a phone number.');
      return;
    }
    try {
      setSaving(true);
      await onSubmit(selectedId, phone.trim());
    } catch (err) {
      console.error('EditEmergencyContactModal save failed:', err);
      Alert.alert('Error', 'Failed to update emergency contact. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Edit Emergency Contact</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn} disabled={saving}>
              <FeatherIcon name="x" size={20} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            <Text style={styles.fieldLabel}>Contact Person</Text>
            <View style={styles.optionList}>
              {options.length === 0 ? (
                <Text style={styles.emptyText}>No family contacts available.</Text>
              ) : (
                options.map((opt: any) => {
                  const selected = opt.id === selectedId;
                  return (
                    <TouchableOpacity
                      key={opt.id}
                      style={[styles.optionRow, selected && styles.optionRowSelected]}
                      onPress={() => handleSelect(opt)}
                    >
                      <View>
                        <Text style={[styles.optionName, selected && styles.optionNameSelected]}>{opt.label}</Text>
                        <Text style={styles.optionRelation}>{opt.relation}</Text>
                      </View>
                      {selected && <FeatherIcon name="check-circle" size={18} color="#1E40AF" />}
                    </TouchableOpacity>
                  );
                })
              )}
            </View>

            <Text style={[styles.fieldLabel, { marginTop: 16 }]}>Emergency Phone Number</Text>
            <TextInput
              style={styles.input}
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
            />
          </ScrollView>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={saving}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color="#FFF" size="small" /> : <Text style={styles.saveText}>Save</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  title: {
    fontSize: 17,
    fontWeight: '800',
    color: '#1E293B',
  },
  closeBtn: {
    padding: 4,
  },
  body: {
    maxHeight: 420,
  },
  bodyContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#94A3B8',
    fontStyle: 'italic',
  },
  optionList: {
    gap: 8,
  } as any,
  optionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#F8FAFC',
  },
  optionRowSelected: {
    borderColor: '#1E40AF',
    backgroundColor: '#EFF6FF',
  },
  optionName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1E293B',
  },
  optionNameSelected: {
    color: '#1E40AF',
  },
  optionRelation: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
  },
  cancelText: {
    color: '#475569',
    fontWeight: '700',
    fontSize: 14,
  },
  saveBtn: {
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 10,
    backgroundColor: '#1E40AF',
    minWidth: 90,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
});
